// WEB-PHASE-1A-SOCKET → FIREBASE-ZERO 6G — shared Socket.IO provider.
//
// 6G ALIGNMENT: this provider now consumes the API's REAL realtime envelopes
// (proven live in 6E and emitted by API 6B/6C):
//   - channel "event"   -> { type, payload, at }     (generic event envelope)
//   - channel "refresh" -> { kind, data, at }         (offer/wallet refresh ping)
// It exposes a typed subscribe() API + the latest envelope + connection status,
// so future Web status listeners (6H+) can consume realtime reliably and fall
// back to their own API polling when the socket is unavailable.
//
// Scope guard (unchanged): transport-only. Moves NO money/business state; the
// envelope is a "something changed, refetch the authoritative REST value" hint.
// Firebase listeners across the app stay fully active and authoritative until a
// later, separately-approved phase migrates each one.
//
// Safety properties:
//   - Disabled unless REACT_APP_SOCKET_REFRESH is "shadow" or "on" (default off
//     => never connects; consumers run on API/polling).
//   - Module-level singleton => exactly one connection per tab.
//   - Every socket interaction is guarded; an invalid/expired token surfaces as
//     a logged `connect_error`, never an unhandled throw or a React crash.
//   - BACK-COMPAT: the raw `socket` and `connected` are still exposed, so the
//     6F notification bell (which does `socket.on("event", ...)` directly) keeps
//     working byte-for-byte.

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { io } from "socket.io-client";
import {
  SOCKET_MODE,
  SOCKET_PATH,
  isSocketEnabled,
  getSocketOrigin,
  getAuthToken,
} from "./socketConfig";

const LOG = "[socket]";

// Minification-proof logger. The production webpack config strips bare
// console.log/info/debug via terser `pure_funcs` (keeps warn/error). We bind the
// console methods to locals at load time so the observability logs survive while
// keeping their real level.
const _bind = (m) => {
  try {
    return (console[m] || console.log).bind(console);
  } catch (e) {
    return () => {};
  }
};
const slog = { info: _bind("info"), debug: _bind("debug"), warn: _bind("warn") };

// ---------------------------------------------------------------------------
// Module-level state. Held outside React so StrictMode/double-mount or an
// accidental second provider can never open a second connection or fork the
// subscriber registry.
// ---------------------------------------------------------------------------
let singletonSocket = null;
let singletonRefCount = 0;

// Subscriber registry: Map<eventType, Set<handler>>. The special type "*"
// receives EVERY envelope. Lives at module scope so subscribe() works the same
// regardless of which component registered it and survives re-renders.
const subscribers = new Map();
// React-state notifiers for the "latest envelope" convenience value.
const latestListeners = new Set();

function fireType(type, env) {
  const set = subscribers.get(String(type));
  if (!set) return;
  // copy first: a handler may unsubscribe (mutate the set) during iteration.
  for (const h of Array.from(set)) {
    try {
      h(env);
    } catch (e) {
      slog.warn(`${LOG} subscriber threw (swallowed):`, e?.message || e);
    }
  }
}

// Dispatch one envelope: its own type, any extra synthetic types, then the
// wildcard, then the React latest-listeners. Never throws.
function dispatchEnvelope(env, extraTypes) {
  if (!env || typeof env !== "object" || !env.type) return;
  fireType(env.type, env);
  if (extraTypes && extraTypes.length) {
    for (const t of extraTypes) fireType(t, env);
  }
  fireType("*", env);
  for (const l of Array.from(latestListeners)) {
    try {
      l(env);
    } catch (ignored) {
      // a latest-listener (React setState) must never break dispatch
      void ignored;
    }
  }
}

// Public-ish registration primitive. Returns an idempotent unsubscribe.
function addSubscriber(type, handler) {
  const key = String(type || "*");
  if (!subscribers.has(key)) subscribers.set(key, new Set());
  subscribers.get(key).add(handler);
  return () => {
    const set = subscribers.get(key);
    if (!set) return;
    set.delete(handler);
    if (set.size === 0) subscribers.delete(key);
  };
}

function createSingletonSocket() {
  if (singletonSocket) return singletonSocket;

  const token = getAuthToken();
  if (!token) {
    // No session yet — nothing to authenticate. Stay idle; the provider
    // re-evaluates on token changes (see effect below).
    slog.info(`${LOG} idle: no auth token, socket not connected`);
    return null;
  }

  const origin = getSocketOrigin();
  let socket;
  try {
    socket = io(origin, {
      path: SOCKET_PATH,
      transports: ["websocket"], // websocket-only, per API (no sticky sessions)
      auth: { token },
      withCredentials: false,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
    });
  } catch (e) {
    slog.warn(`${LOG} failed to construct socket (non-fatal):`, e?.message || e);
    return null;
  }

  // --- Connection lifecycle (logs only) ---
  socket.on("connect", () => {
    slog.info(`${LOG} connected`, { id: socket.id, origin, path: SOCKET_PATH });
  });
  socket.on("disconnect", (reason) => {
    slog.info(`${LOG} disconnected:`, reason);
  });
  // Invalid/expired token or unreachable server lands here. MUST NOT crash —
  // consumers continue on their API/polling fallback.
  socket.on("connect_error", (err) => {
    slog.warn(`${LOG} connect_error (non-fatal, API/polling still live):`, err?.message || err);
  });

  // --- API realtime envelopes (6B/6C) -> typed dispatch ---
  // Generic event envelope: { type, payload, at }.
  socket.on("event", (env) => {
    slog.debug(`${LOG} event:`, env && env.type);
    dispatchEnvelope(env);
  });
  // Refresh ping: { kind, data, at }. Normalized into the SAME dispatch so
  // consumers use one subscribe() API. Delivered under type "refresh" and the
  // kind-specific synthetic type "refresh:<kind>" (e.g. "refresh:offer").
  socket.on("refresh", (r) => {
    const kind = r && r.kind ? String(r.kind) : "";
    const env = {
      type: "refresh",
      kind,
      payload: r && r.data !== undefined ? r.data : null,
      at: r && r.at,
    };
    slog.debug(`${LOG} refresh:`, kind);
    dispatchEnvelope(env, kind ? [`refresh:${kind}`] : undefined);
  });

  singletonSocket = socket;
  return singletonSocket;
}

function teardownSingletonSocket() {
  if (!singletonSocket) return;
  try {
    singletonSocket.removeAllListeners();
    singletonSocket.disconnect();
  } catch (e) {
    // ignore — best-effort cleanup
  }
  singletonSocket = null;
}

const defaultCtx = {
  socket: null,
  mode: SOCKET_MODE,
  connected: false,
  latestEvent: null,
  // No-op subscribe so a consumer rendered WITHOUT the provider never crashes;
  // it simply receives no realtime events and relies on its API fallback.
  subscribe: () => () => {},
};
const SocketContext = createContext(defaultCtx);

export function SocketProvider({ children }) {
  const [connected, setConnected] = useState(false);
  const [latestEvent, setLatestEvent] = useState(null);
  const socketRef = useRef(null);

  useEffect(() => {
    // Disabled flag (default) => render children untouched, never connect.
    if (!isSocketEnabled()) return undefined;

    singletonRefCount += 1;

    // Mirror module latest-envelope into React state for `latestEvent`.
    const onLatest = (env) => setLatestEvent(env);
    latestListeners.add(onLatest);

    const socket = createSingletonSocket();
    socketRef.current = socket;

    if (!socket) {
      // No token yet (or construction failed). Watch for a token appearing
      // (login in another flow) and connect once it does — cheap interval,
      // cleared on unmount.
      let cancelled = false;
      const poll = setInterval(() => {
        if (cancelled || singletonSocket) return;
        if (getAuthToken()) {
          const s = createSingletonSocket();
          socketRef.current = s;
          if (s) {
            s.on("connect", () => setConnected(true));
            s.on("disconnect", () => setConnected(false));
            clearInterval(poll);
          }
        }
      }, 5000);

      return () => {
        cancelled = true;
        clearInterval(poll);
        latestListeners.delete(onLatest);
        singletonRefCount -= 1;
        if (singletonRefCount <= 0) {
          singletonRefCount = 0;
          teardownSingletonSocket();
        }
      };
    }

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    if (socket.connected) setConnected(true);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      latestListeners.delete(onLatest);
      singletonRefCount -= 1;
      // Only tear the shared socket down when the last consumer unmounts
      // (app-level provider => effectively page unload).
      if (singletonRefCount <= 0) {
        singletonRefCount = 0;
        teardownSingletonSocket();
      }
    };
    // Connect once for the app's lifetime; token-appearance handled by the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Typed subscription. `type` is an API event type ("offer.status.changed",
  // "order.status.changed", "notification.count", "refresh", "refresh:offer",
  // "refresh:wallet", ...) or "*" for all. Returns an unsubscribe fn. Safe to
  // call before the socket connects — the registry is module-level, so handlers
  // registered early still receive events once the connection is established.
  const subscribe = useCallback((type, handler) => {
    if (typeof handler !== "function") return () => {};
    return addSubscriber(type, handler);
  }, []);

  const value = {
    socket: socketRef.current,
    mode: SOCKET_MODE,
    connected,
    latestEvent,
    subscribe,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
}

// Hook for consumers. Shape: { socket, connected, mode, latestEvent, subscribe }.
//
// HOW FUTURE STATUS LISTENERS SHOULD SUBSCRIBE (6H+):
//   const { subscribe, connected } = useSocket();
//   useEffect(() => {
//     fetchStatusFromApi();                         // initial + source of truth
//     const off = subscribe("order.status.changed", () => fetchStatusFromApi());
//     const poll = setInterval(() => { if (!connected) fetchStatusFromApi(); }, 60000);
//     return () => { off(); clearInterval(poll); };
//   }, [subscribe, connected]);
// The socket carries only a "refetch" hint; the API/Mongo value stays
// authoritative, and the polling fallback covers a disabled/dropped socket.
export function useSocket() {
  return useContext(SocketContext);
}

export default SocketProvider;
